import {
  isFileUIPart,
  isToolUIPart,
  type ChatStatus,
  type SourceDocumentUIPart,
} from "ai";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, type ReactNode, type Ref } from "react";
import { useStickToBottom } from "use-stick-to-bottom";

/**
 * Shared visual layer for chat surfaces. Owns the message list (text, image,
 * attachment, reasoning, and tool-call parts), auto-scroll, the typing-dots
 * placeholder, the queued-message strip, and the composer. Stateless about
 * transport — a host wires `useChat`, a live document feed, or anything else and
 * passes in `messages`, `status`, and `onSend`.
 *
 * Everything host-specific arrives as a prop declared in `types.ts`: the tool
 * cards a host draws itself, its approval copy, where attachment bytes go, where
 * `@`-mention files come from. Any improvement here — tool-call rendering,
 * attachment UI, composer affordances — reaches every surface built on it.
 */
import { cn } from "../lib/utils";
import { AgentMarkdown, type WikiLinkRenderer } from "./AgentMarkdown";
import { collectSources, groupBodyItems } from "./body-items";
import { ChatFilePart } from "./ChatFilePart";
import { ChatImagePart } from "./ChatImagePart";
import {
  CompactionIndicator,
  type CompactionMetadata,
} from "./CompactionIndicator";
import { Composer } from "./Composer";
import { ContextChips } from "./ContextChips";
import { MessageSources } from "./MessageSources";
import { QueuedMessages } from "./QueuedMessages";
import { ReasoningView } from "./ReasoningView";
import { ToolCallGroup, ToolCallView, ToolHostProvider } from "./ToolCallView";
import type {
  AnyUIMessage,
  AnyUIPart,
  ApprovalCopy,
  ContextRef,
  FileContextProvider,
  RenderToolCard,
  RespondToApproval,
  SentMessage,
  StandsAlone,
  UploadAttachment,
} from "./types";
import { useOnline } from "./use-online";
import { useDropHighlight } from "./useDropHighlight";
import { isContextTextPart } from "./workspace-context";

export type { SentMessage } from "./types";

export interface ChatPaneProps {
  messages: AnyUIMessage[];
  status: ChatStatus;
  /**
   * Set when the turn failed (`status === "error"`). Surfaced as an inline
   * notice so a died thread reads as "errored", not silently stalled.
   */
  error?: Error;
  onSend: (message: SentMessage) => void;
  /**
   * Abort the in-flight turn. Wired to the composer's Stop button (shown while a
   * turn streams and the composer is empty). Omit on surfaces with no turn to
   * stop.
   */
  onStop?: () => void;
  /** Messages lined up behind the running turn. */
  queued?: SentMessage[];
  /**
   * Drop a queued message. Supply it when the host still holds the message
   * client-side; omit when the send is already committed and can't be recalled.
   */
  onRemoveQueued?: (index: number) => void;
  /**
   * Answer an open tool-approval request. Omit on surfaces with no
   * approval-gated tools and no card renders.
   */
  onToolApproval?: RespondToApproval;
  /** Draw a host-specific card for a tool part. See {@link RenderToolCard}. */
  renderToolCard?: RenderToolCard;
  /**
   * A message the host shows as one activity line instead of a bubble, e.g.
   * a comment that asked the agent, whose full text lives in its thread.
   * Return undefined to render the message as usual.
   */
  renderActivity?: (message: AnyUIMessage) => ReactNode | undefined;
  /** The words on a generic approval card, when the host knows its own tools. */
  approvalCopy?: (modelName: string, input: unknown) => ApprovalCopy;
  /** Tool parts that must not fold into a collapsed run. */
  standsAlone?: StandsAlone;
  /** Render an in-app `[[wiki-link]]` with the host's router. */
  wikiLink?: WikiLinkRenderer;
  /**
   * Is the live connection behind this transcript up? While it's false the
   * transcript may be behind the server, so the pane shows a thin "Reconnecting"
   * bar above the messages. Defaults to connected, for surfaces with no live
   * socket.
   */
  connected?: boolean;
  /** Optional toolbar/banner rendered above the message list. */
  header?: ReactNode;
  /**
   * Replaces the composer entirely — a paywall, a read-only notice. One panel,
   * one message, the way forward: a dead textarea invites clicks.
   */
  composerReplacement?: ReactNode;
  /** Replaces the generic error notice for errors the host renders better. */
  renderError?: (error: Error) => ReactNode | undefined;
  /** Shown when there are no visible messages and nothing is streaming. */
  emptyState?: ReactNode;
  composerPlaceholder?: string;
  /** Disable the composer (e.g. while a prior operation completes). */
  composerDisabled?: boolean;
  /** Show the attachment affordance (file picker + paste + drop). */
  allowAttachments?: boolean;
  /** Media types the picker accepts, as an `accept` attribute value. */
  attachmentAccept?: string;
  maxFileSize?: number;
  /** Where attachment bytes go. Omit to send `data:` URLs as-is. */
  uploadAttachment?: UploadAttachment;
  /**
   * Enable per-message file context: an open-file chip + `@`-mention
   * autocomplete. Omit on surfaces with no workspace behind them.
   */
  fileContext?: FileContextProvider | null;
  /** Path of the file the user is currently viewing — auto-attached as context. */
  openFilePath?: string | null;
  /** Host controls in the composer footer — a model picker, an effort menu. */
  composerFooterExtras?: (state: { disabled: boolean }) => ReactNode;
  /**
   * Forward to the composer's textarea. A mobile dock uses this to call
   * `focus()` synchronously inside a tap handler so iOS opens the keyboard.
   */
  composerRef?: Ref<HTMLTextAreaElement>;
  /** Focus the composer on mount (a just-created chat). */
  autoFocusComposer?: boolean;
}

export function ChatPane({
  messages,
  status,
  error,
  onSend,
  onStop,
  queued,
  onRemoveQueued,
  onToolApproval,
  renderToolCard,
  renderActivity,
  approvalCopy,
  standsAlone,
  wikiLink,
  connected = true,
  header,
  composerReplacement,
  renderError,
  emptyState,
  composerPlaceholder = "Ask anything…",
  composerDisabled = false,
  allowAttachments = false,
  attachmentAccept,
  maxFileSize,
  uploadAttachment,
  fileContext,
  openFilePath = null,
  composerFooterExtras,
  composerRef,
  autoFocusComposer = false,
}: ChatPaneProps) {
  const online = useOnline();
  const isStreaming = status === "streaming" || status === "submitted";
  const { isDragging, dragHandlers } = useDropHighlight();

  // Auto-scroll on new content, but don't fight the user. `use-stick-to-bottom`
  // owns the "follow the bottom while the user is already there" logic via a
  // ResizeObserver on the content: any height change — a streamed token, a tool
  // line expanding, or a late-laying-out card like the tool-approval banner —
  // keeps us pinned unless the user has scrolled up. Watching the DOM directly
  // (rather than proxying content growth through part counts / text lengths) is
  // what fixes cards that mount without changing those proxies from being left
  // off-screen.
  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom({
    initial: "instant",
    resize: "smooth",
  });

  const lastMessage = messages[messages.length - 1];

  // Sending a message is an explicit "take me to the bottom" gesture — force it
  // even if the user had scrolled up while reading (which otherwise escapes the
  // ResizeObserver's auto-follow).
  const messageCount = messages.length;
  useEffect(() => {
    if (lastMessage?.role === "user") void scrollToBottom();
  }, [messageCount, lastMessage?.role, scrollToBottom]);

  // Queuing a message mid-turn is its own "take me to the bottom" gesture —
  // keyed on the count alone so it fires once when an item is added.
  const queuedCount = queued?.length ?? 0;
  const prevQueuedCount = useRef(queuedCount);
  useEffect(() => {
    if (queuedCount > prevQueuedCount.current) void scrollToBottom();
    prevQueuedCount.current = queuedCount;
  }, [queuedCount, scrollToBottom]);

  // The composer stays live while a turn streams: typing + submitting queues
  // the next message, and the submit control doubles as a Stop button. Only an
  // explicit disable or going offline locks it.
  // Show the standalone "thinking" bubble whenever a trailing user message is
  // still waiting on its assistant reply. `isStreaming` (from `status`) catches
  // this client's own turns; the bare trailing-user-message check catches one
  // nobody here started (an email or scheduled turn, or another tab), which
  // arrives over a live feed while this client is still "ready". A failed turn
  // shows the error notice below instead of dots.
  const awaitingAssistant =
    status !== "error" && (isStreaming || lastMessage?.role === "user");

  // When the failed turn already carries a structured `data-error` part (which
  // renders its own inline notice), skip the generic banner so we don't show the
  // error twice.
  const lastMessageHasStructuredError =
    lastMessage?.parts.some((p) => p.type === "data-error") ?? false;

  const textareaDisabled = composerDisabled || !online;
  const effectivePlaceholder = !online
    ? "Chat unavailable when offline"
    : composerPlaceholder;
  const offlineTitle = !online ? "Chat unavailable when offline" : undefined;
  const hostedError =
    status === "error" && error ? renderError?.(error) : undefined;

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {header}
        {/* Two ways to be behind the server, and a socket only reports one of
          them: losing the network doesn't reliably close the WebSocket (the OS
          can drop TCP without a FIN), so the host's own online signal is the
          second, independent witness. Either one means the transcript below is
          whatever we last received. */}
        {!connected || !online ? <ReconnectingBar online={online} /> : null}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
          <div ref={contentRef} className="px-4 py-6">
            <ToolHostProvider
              respond={onToolApproval}
              renderToolCard={renderToolCard}
              approvalCopy={approvalCopy}
            >
              <ul className="mx-auto flex max-w-2xl flex-col gap-3">
                {messages.length === 0 && !isStreaming
                  ? (emptyState ?? (
                      <li className="py-12 text-center text-sm text-muted-foreground">
                        Start the conversation.
                      </li>
                    ))
                  : null}
                {messages.map((m, i) => {
                  // Surface-generated messages carry `metadata.hidden` — the
                  // model sees them, the member doesn't.
                  if (isHiddenMessage(m)) return null;
                  const activity = renderActivity?.(m);
                  if (activity) {
                    return (
                      <li key={m.id} className="self-stretch">
                        {activity}
                      </li>
                    );
                  }
                  const isLast = i === messages.length - 1;
                  // A turn may reserve its assistant row when the stream starts,
                  // so the transcript has an anchor for anything appended
                  // mid-turn. While that row is streaming it renders as the typing
                  // indicator; a turn that ended without producing one leaves it
                  // empty, and an empty row is not a message.
                  if (
                    m.role === "assistant" &&
                    m.parts.length === 0 &&
                    !(isLast && isStreaming)
                  )
                    return null;
                  // Only the user's own turns read as chat bubbles. The assistant
                  // takes the full content column like a document (no bubble, no
                  // background) so its markdown — headings, lists, tables — has room
                  // to breathe, especially on mobile.
                  const bubbleClassName =
                    m.role === "user"
                      ? "user-bubble rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground"
                      : "text-sm text-foreground";
                  return (
                    <li
                      key={m.id}
                      className={
                        m.role === "user"
                          ? "max-w-[80%] self-end"
                          : "self-stretch"
                      }
                    >
                      <div className={bubbleClassName}>
                        <MessageBody
                          message={m}
                          isStreaming={isLast && isStreaming}
                          standsAlone={standsAlone}
                          wikiLink={wikiLink}
                        />
                      </div>
                    </li>
                  );
                })}
                {awaitingAssistant && lastMessage?.role !== "assistant" ? (
                  <li className="self-start text-sm text-foreground">
                    <TypingDots />
                  </li>
                ) : null}
                {status === "error" && !lastMessageHasStructuredError
                  ? (hostedError ?? (
                      <li
                        role="alert"
                        className="max-w-[80%] self-start rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive"
                      >
                        {error?.message
                          ? `The agent hit an error: ${error.message}`
                          : "The agent hit an error and couldn't finish. Try sending your message again."}
                      </li>
                    ))
                  : null}
                {/* Accepted-but-not-yet-driven sends sit below the running turn's
                  activity, where they'll land once they're taken. */}
                {queued ? (
                  <QueuedMessages queued={queued} onRemove={onRemoveQueued} />
                ) : null}
              </ul>
            </ToolHostProvider>
          </div>
        </div>
        <div className="shrink-0 border-t border-border px-5 pt-3 pb-edge">
          {composerReplacement ? (
            <div className="mx-auto max-w-2xl">{composerReplacement}</div>
          ) : (
            <div
              className={cn(
                "mx-auto max-w-2xl rounded-md ring-offset-2 ring-offset-background transition-shadow",
                allowAttachments && isDragging && online && "ring-2 ring-ring",
              )}
              title={offlineTitle}
              {...(allowAttachments && online ? dragHandlers : {})}
            >
              <Composer
                onSend={onSend}
                status={status}
                onStop={onStop}
                placeholder={effectivePlaceholder}
                textareaDisabled={textareaDisabled}
                submitDisabled={textareaDisabled}
                allowAttachments={allowAttachments}
                attachmentAccept={attachmentAccept}
                maxFileSize={maxFileSize}
                uploadAttachment={uploadAttachment}
                fileContext={fileContext}
                openFilePath={openFilePath}
                composerRef={composerRef}
                autoFocus={autoFocusComposer}
                footerExtras={composerFooterExtras}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Thin bar between the header and the transcript, shown while the chat socket
 * is down. The bar's presence is the message — a strip of chrome that isn't
 * normally there, sitting directly above the transcript it's qualifying. No
 * action, no dismissal; it clears itself once the connection is back and the
 * server has re-sent the thread.
 *
 * Offline swaps the word because the two differ in what happens next:
 * "reconnecting" promises it will fix itself, which it won't with no network.
 * Same bar either way, so a flaky connection doesn't make the layout jump.
 */
function ReconnectingBar({ online }: { online: boolean }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex shrink-0 items-center justify-center gap-2 border-b border-border bg-muted/60 px-4 py-1.5 text-xs text-muted-foreground"
    >
      {online ? (
        <>
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Reconnecting…</span>
        </>
      ) : (
        <span>Offline</span>
      )}
    </div>
  );
}

function TypingDots() {
  return (
    <span
      className="inline-flex items-center gap-1 py-1"
      aria-label="Assistant is thinking"
    >
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.2s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.1s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60" />
    </span>
  );
}

/** Surface-generated messages the pane must not paint — sent with
 *  `metadata.hidden` (see `SentMessage.hidden`). The model still sees them. */
function isHiddenMessage(m: AnyUIMessage): boolean {
  return (m.metadata as { hidden?: boolean } | undefined)?.hidden === true;
}

export interface MessageBodyProps {
  message: AnyUIMessage;
  isStreaming: boolean;
  standsAlone?: StandsAlone;
  wikiLink?: WikiLinkRenderer;
}

export function MessageBody({
  message,
  isStreaming,
  standsAlone,
  wikiLink,
}: MessageBodyProps) {
  const isAssistant = message.role === "assistant";
  if (message.parts.length === 0 && isAssistant && isStreaming) {
    return <TypingDots />;
  }
  // One render path for both roles — same `renderPart`, same markdown
  // pipeline (so wiki-links, code blocks, etc. work identically). The
  // bubble-specific styling (white-on-primary for user, prose-invert for
  // assistant) is passed via `textClassName`. Context chips are derived
  // from `source-document` parts and rendered above the body; the assistant's
  // web citations (`source-url`) render as one list below it.
  const refs: ContextRef[] = message.parts
    .filter((p): p is SourceDocumentUIPart => p.type === "source-document")
    .map((p) => ({ path: p.sourceId, source: "mention" }));
  const sources = isAssistant ? collectSources(message) : [];
  // Streamdown paints code blocks on `bg-background`, so prose's light-on-dark
  // `pre` text color would vanish on them: unhighlighted code needs foreground.
  // The user bubble's text override skips code so its token colors survive.
  const textClassName = isAssistant
    ? "prose prose-sm dark:prose-invert max-w-none [&_pre]:text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
    : "prose prose-sm max-w-none [&_*:not(pre_*)]:!text-primary-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0";
  // One rule: while the turn is live, show trailing dots unless the tail
  // part already animates its own cue (streaming text/reasoning, a running
  // tool's spinner, an approval card). Settled anything falls through to
  // dots, so a mid-turn think can never look stalled.
  const lastPart = message.parts[message.parts.length - 1] as
    | { type: string; state?: string }
    | undefined;
  const tailHasOwnCue =
    !!lastPart &&
    (lastPart.type === "text" || lastPart.type === "reasoning"
      ? lastPart.state === "streaming"
      : lastPart.type.startsWith("tool-") || lastPart.type === "dynamic-tool"
        ? ["input-streaming", "input-available", "approval-requested"].includes(
            lastPart.state ?? "",
          )
        : false);
  const showTrailingDots = isAssistant && isStreaming && !tailHasOwnCue;
  return (
    <div className="space-y-1.5">
      {refs.length > 0 ? <ContextChips refs={refs} /> : null}
      {groupBodyItems(message, standsAlone).map((item) =>
        item.kind === "tools" ? (
          <ToolCallGroup key={item.key} parts={item.parts} />
        ) : (
          renderPart(item.part, item.key, textClassName, !isAssistant, wikiLink)
        ),
      )}
      {sources.length > 0 ? <MessageSources sources={sources} /> : null}
      {showTrailingDots ? <TypingDots /> : null}
    </div>
  );
}

function renderPart(
  part: AnyUIPart,
  key: string,
  textClassName: string,
  inverted: boolean,
  wikiLink?: WikiLinkRenderer,
) {
  if (part.type === "text") {
    // Hidden `<workspace_context>` block — for the model, not the bubble.
    if (isContextTextPart(part.text)) return null;
    return (
      <AgentMarkdown
        key={key}
        className={textClassName}
        inverted={inverted}
        wikiLink={wikiLink}
      >
        {part.text}
      </AgentMarkdown>
    );
  }
  // An image gets a thumbnail; every other attachment gets an openable chip.
  // Both are rendered — a file the member attached must appear in their own
  // transcript, not silently vanish because it isn't an image.
  if (isFileUIPart(part)) {
    return part.mediaType.startsWith("image/") ? (
      <ChatImagePart key={key} part={part} />
    ) : (
      <ChatFilePart key={key} part={part} />
    );
  }
  // `isToolUIPart` matches both static and dynamic tool parts. App-published
  // tools are built with `tool()`, so they arrive as typed
  // `tool-<slug>__<tool>` parts — `ToolCallView` strips the slug for display.
  if (isToolUIPart(part)) {
    return <ToolCallView key={key} part={part} />;
  }
  // Reasoning models stream their think-aloud as `reasoning` parts; surface
  // them as a live "thinking" bubble so heavy turns don't read as frozen.
  if (part.type === "reasoning") {
    return <ReasoningView key={key} part={part} />;
  }
  // A structured, persisted failure — rendered with the raw provider reason
  // tucked behind a disclosure.
  if (part.type === "data-error") {
    const error = asTurnError(part.data);
    return error ? <ErrorPart key={key} error={error} /> : null;
  }
  // The host compacted the thread here: everything above is a compressed
  // account of the conversation, everything below is verbatim.
  if (part.type === "data-compaction") {
    const compaction = asCompaction(part.data);
    return compaction ? (
      <CompactionIndicator
        key={key}
        summary={compaction.summary}
        metadata={compaction.metadata}
      />
    ) : null;
  }
  // `source-document` (the chip strip) and `source-url` (the source list) are
  // both rendered per-message rather than per-part; `step-start` carries no
  // content of its own.
  return null;
}

/** A structured turn failure a host persisted on the message. */
export interface TurnErrorData {
  /** A short, friendly sentence shown to the user verbatim. */
  message: string;
  /** The provider's own reason, for a "details" disclosure. */
  providerReason?: string;
  /** The provider's HTTP status, when the failure was an API call. */
  statusCode?: number;
}

/** Data parts are `unknown` at this end of the SDK's typing — check the shape. */
function asTurnError(data: unknown): TurnErrorData | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  return typeof d.message === "string" ? (d as unknown as TurnErrorData) : null;
}

function asCompaction(
  data: unknown,
): { summary: string; metadata: CompactionMetadata } | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const metadata = d.metadata as CompactionMetadata | undefined;
  if (typeof d.summary !== "string" || !metadata) return null;
  if (typeof metadata.originalTokenEstimate !== "number") return null;
  return { summary: d.summary, metadata };
}

/**
 * Renders a structured turn failure inline in the transcript. The friendly
 * `message` is chosen by the host; the raw provider reason — often long JSON —
 * is tucked behind a disclosure so a power user can see exactly what the model
 * said without it dominating the UI.
 */
function ErrorPart({ error }: { error: TurnErrorData }) {
  return (
    <div
      role="alert"
      className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    >
      <p>{error.message}</p>
      {error.providerReason && error.providerReason !== error.message ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs opacity-70 hover:opacity-100">
            Details
          </summary>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words text-xs opacity-80">
            {error.statusCode ? `HTTP ${error.statusCode}\n` : ""}
            {error.providerReason}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
